import { useEffect } from "react";
import { createPortal } from "react-dom";
import { REDUCE_MOTION } from "../../lib/theme";
import { BuzerIcon } from "../../BuzerIcons";
import { st } from "../../styles.js";
import { EFFECTS } from "../../lib/constants";

const EFFECT_META = Object.fromEntries(EFFECTS.map((e) => [e.code, e]));

// v24 — celebration burst (confetti or a shower of a chosen glyph), falling from the top of the
// screen. Used for the "pri otvorení appky" (app-open) effect + the whole-app "perfektný deň"/
// level-up celebration — NOT for single-habit completion (see CompletionBurst below, f-batch).
// Honours reduced-motion.
export function EffectBurst({ effect }) {
  if (!effect || REDUCE_MOTION) return null;
  // v26.3 §17 — Lite: jemný „glow" namiesto konfiet — pár pomaly stúpajúcich mäkkých bodiek
  if (effect === "calm") {
    return (
      <div style={st.confettiWrap}>
        {Array.from({ length: 10 }).map((_, i) => (
          <span key={i} className="calm-dot" style={{ left: `${8 + (i * 84) / 9}%`,
            animationDelay: `${(i % 5) * 0.12}s`, animationDuration: `${1.6 + (i % 4) * 0.2}s` }} />
        ))}
      </div>
    );
  }
  if (effect === "confetti") {
    return (
      <div style={st.confettiWrap}>
        {Array.from({ length: 40 }).map((_, i) => (
          <span key={i} className="confetti" style={{ left: `${(i * 53) % 100}%`,
            background: ["var(--confetti-1)", "var(--confetti-2)", "var(--confetti-3)", "var(--confetti-4)", "var(--confetti-5)"][i % 5],
            animationDelay: `${(i % 5) * 0.1}s`, animationDuration: `${1.8 + (i % 6) * 0.2}s` }} />
        ))}
      </div>
    );
  }
  const m = EFFECT_META[effect];
  if (!m || !m.icon) return null;
  return (
    <div style={st.confettiWrap}>
      {Array.from({ length: 26 }).map((_, i) => (
        <span key={i} className="confetti-glyph" style={{ left: `${(i * 61) % 100}%`,
          animationDelay: `${(i % 6) * 0.12}s`, animationDuration: `${2 + (i % 5) * 0.25}s` }}>
          <BuzerIcon name={m.icon} size={18 + (i % 3) * 4} color={m.color} />
        </span>
      ))}
    </div>
  );
}

// f-batch — completion burst: the ONE effect for finishing a habit, everywhere (habit-row tap,
// habit-detail modal, Obchod „po splnení návyku" preview). Particles always SPREAD OUT from the
// tap point (rect) — never fall from the top — regardless of the purchased `effect` variant.
//   effect — EFFECTS code ("confetti" default = the original pestré „guličky" — round, multi-
//            coloured particle balls; hearts/clover/star = the same radial burst, shaped as that
//            glyph) or falsy (defaults to the balls).
//   rect   — origin {x,y,w,h} in viewport coords (habit row, or the tapped button in the shop).
//   onDone — called ~1.55s later so the caller can unmount/clear state.
//   tile   — show the glowing rectangle behind the burst (the habit row/button flashing); off
//            when there's no real UI element to flash (rare).
export function CompletionBurst({ effect, rect, onDone, tile = true }) {
  useEffect(() => { const t = setTimeout(onDone, 1550); return () => clearTimeout(t); }, [onDone]);
  if (REDUCE_MOTION || !rect) return null;
  const { x, y, w, h } = rect;
  const N = 18;
  const m = effect && effect !== "confetti" ? EFFECT_META[effect] : null;
  return createPortal(
    <div className="bz-cfx" aria-hidden="true">
      {tile && <div className="bz-cfx__tile" style={{ left: x, top: y, width: w, height: h }} />}
      {Array.from({ length: N }).map((_, i) => {
        const pos = { left: x + (w * (i + 0.5)) / N, top: y + h / 2, "--a": `${Math.round((i / N) * 360)}deg` };
        return m && m.icon
          ? <span key={i} className="bz-cfx__gp" style={pos}><BuzerIcon name={m.icon} size={13} color={m.color || "var(--accent)"} /></span>
          : <span key={i} className="bz-cfx__p" style={pos} />;
      })}
    </div>,
    document.body,
  );
}
